import {useEffect, useState} from 'react'
import {Col, Modal, Row} from 'react-bootstrap'
import {useLazyQuery} from '@apollo/client'
import {defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'

import {GET_ONE_PIPELINE} from '../../../gql/queries/pipelines'
import {IResPipelines, NodePipelines} from '../../../types/pipelines'
import {parseId} from '../../../helpers'
import {PipelineInfoTab} from './tabs/PipelineInfoTab'
import {PipelineGroupsTab, PipelineReviewersTab} from './tabs/PipelineAssigneesTab'
import {PipelineRulesTab} from './tabs/PipelineRulesTab'

export type TPipelineTab = 'info' | 'groups' | 'reviewers' | 'rules'

interface IModalEditPipelineProps {
  isOpen: boolean
  onClose: () => void
  /** Se refresca la lista de pipelines al cerrar, por si cambiaron nombre o stages. */
  refetch: () => void
  pipeline: {id: string; name: string}
  initialTab?: TPipelineTab
}

const epM = defineMessages({
  title: {id: 'leads.pipelines.edit.title', defaultMessage: 'Edit pipeline'},
  infoName: {id: 'leads.pipelines.edit.infoName', defaultMessage: 'Pipeline info'},
  infoDesc: {id: 'leads.pipelines.edit.infoDesc', defaultMessage: 'Name, organization and stages'},
  groupsName: {id: 'leads.pipelines.edit.groupsName', defaultMessage: 'Groups'},
  groupsDesc: {
    id: 'leads.pipelines.edit.groupsDesc',
    defaultMessage: 'Users that receive the leads',
  },
  reviewersName: {id: 'leads.pipelines.edit.reviewersName', defaultMessage: 'Reviewers'},
  reviewersDesc: {
    id: 'leads.pipelines.edit.reviewersDesc',
    defaultMessage: 'Users that can see every lead',
  },
  rulesName: {id: 'leads.pipelines.edit.rulesName', defaultMessage: 'Assignment rules'},
  rulesDesc: {
    id: 'leads.pipelines.edit.rulesDesc',
    defaultMessage: 'Quotas per group for lead distribution',
  },
  notFound: {id: 'leads.stages.pipelineNotFound', defaultMessage: 'Pipeline not found'},
})

export const ModalEditPipeline: React.FC<IModalEditPipelineProps> = ({
  isOpen,
  onClose,
  refetch,
  pipeline,
  initialTab = 'info',
}) => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage(
      {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
      {message}
    )

  const [tab, setTab] = useState<TPipelineTab>(initialTab)

  const [gqlGetPipeline, {data, loading, error, refetch: refetchPipeline}] =
    useLazyQuery<IResPipelines>(GET_ONE_PIPELINE, {fetchPolicy: 'no-cache'})

  // parseId devuelve string | number segun el formato del id; se normaliza a string.
  const pipelineNumericId = String(parseId(pipeline.id))

  useEffect(() => {
    if (!isOpen) return
    gqlGetPipeline({variables: {id: pipelineNumericId}})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pipelineNumericId])

  useEffect(() => {
    if (error) toast.error(errMsg(error.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const info: NodePipelines | undefined = data?.allPipelines?.edges?.[0]?.node

  const tabs: {key: TPipelineTab; icon: string; name: string; description: string}[] = [
    {
      key: 'info',
      icon: 'bi bi-diagram-3',
      name: intl.formatMessage(epM.infoName),
      description: intl.formatMessage(epM.infoDesc),
    },
    {
      key: 'groups',
      icon: 'bi bi-people',
      name: intl.formatMessage(epM.groupsName),
      description: intl.formatMessage(epM.groupsDesc),
    },
    {
      key: 'reviewers',
      icon: 'bi bi-eye',
      name: intl.formatMessage(epM.reviewersName),
      description: intl.formatMessage(epM.reviewersDesc),
    },
    {
      key: 'rules',
      icon: 'bi bi-sliders',
      name: intl.formatMessage(epM.rulesName),
      description: intl.formatMessage(epM.rulesDesc),
    },
  ]

  const handleClose = () => {
    // La lista de pipelines muestra nombre y stages, así que se refresca al salir.
    refetch()
    onClose()
  }

  return (
    <Modal
      className='modal-add-policy'
      backdrop='static'
      keyboard={false}
      show={isOpen}
      onHide={handleClose}
      size='xl'
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage(epM.title)}
          {info?.name ? <span className='text-muted fs-5 ms-2'>{info.name}</span> : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col
            xl={3}
            lg={4}
            className='d-none d-md-none d-lg-block'
            style={{borderRight: '1px solid #eeeef4'}}
          >
            <div className='modal-tabsoptions-left'>
              {tabs.map((tb) => (
                <div
                  key={tb.key}
                  className='d-flex align-items-center gap-2 p-5 rounded'
                  style={{
                    backgroundColor: tb.key === tab ? '#e7e7ff' : undefined,
                    position: 'relative',
                    maxWidth: '330px',
                  }}
                  role='button'
                  onClick={() => setTab(tb.key)}
                >
                  {tb.key === tab && <div className='tabs-options-left' />}
                  <div>
                    <i className={`${tb.icon} fs-1`} style={{color: 'inherit'}} />
                  </div>
                  <div>
                    <h6 style={{fontSize: '11px'}} className='m-0'>
                      {tb.name}
                    </h6>
                    <p style={{fontSize: '10px'}} className='m-0'>
                      {tb.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Col>
          <Col lg={8} xl={9}>
            {/* Las pestañas de grupos/revisores/reglas no dependen del detalle del
                pipeline, así que se muestran aunque la query de stages siga cargando. */}
            {loading && !info ? (
              <div className='text-muted p-5'>
                {intl.formatMessage({id: 'common.loading', defaultMessage: 'Loading...'})}
              </div>
            ) : (
              <>
                {tab === 'info' &&
                  (info ? (
                    <PipelineInfoTab
                      pipeline={info}
                      pipelineNumericId={pipelineNumericId}
                      refetchPipeline={refetchPipeline}
                    />
                  ) : (
                    <div className='text-muted p-5'>{intl.formatMessage(epM.notFound)}</div>
                  ))}
                {tab === 'groups' && (
                  <PipelineGroupsTab
                    pipelineId={pipeline.id}
                    pipelineName={info?.name ?? pipeline.name}
                  />
                )}
                {tab === 'reviewers' && (
                  <PipelineReviewersTab
                    pipelineId={pipeline.id}
                    pipelineName={info?.name ?? pipeline.name}
                  />
                )}
                {tab === 'rules' && (
                  <PipelineRulesTab
                    pipelineId={pipeline.id}
                    pipelineName={info?.name ?? pipeline.name}
                  />
                )}
              </>
            )}
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  )
}
